import { PayloadAction } from '@reduxjs/toolkit';
import { HoldToken } from '@seatsio/seatsio-react';
import { TicketSalePageEvent } from 'app/pages/TicketSalePage/slice/types';
import {
  ISelectedTicket,
  IUserBilling,
} from 'app/redirects/RedirectToBilling/RedirectToBilling';
import { DateTime } from 'luxon';
import { createSagaAction } from 'saga-toolkit';
import {
  getBillingInfoAction,
  refreshTheAccesTokenAction,
} from 'store/userSlice';
import {
  CartBundle,
  CartItems,
  CartTicket,
  VoucherInterface,
} from 'types/CartItem';
import { isAddon, isTopup, SelectedItemType } from 'types/SelectedItem';
import { Ticket } from 'types/TicketsState';
import { vendorIdAndEventId } from 'types/commonPayloadTypes';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { getCountryIdByCode, getStateIdByCode } from 'utils/countriesAndStates';
import { onIframeScrollIntoView } from 'utils/embed';
import {
  getFeePrice,
  getTaxPrice,
  getTicketPriceByQuantity,
} from 'utils/tickets';
import {
  initialBillingData,
  initialCountdownData,
  initialSelectedEvent,
  initialVoucher,
} from '../../app/pages/TicketSalePage/slice/initialValues';
import { CartState, FetchCartDataPayload } from './types';
import { getIdOrderItemForTicket, getOrderItemsForBundle } from './utils';

const sliceName = 'cart';

export const initialState: CartState = {
  loading: false,
  selectedEvent: initialSelectedEvent,
  selectedItems: [],
  uuid: '',
  holdToken: {
    token: '',
    endTime: '',
  },
  voucher: initialVoucher as VoucherInterface,
  countdown: initialCountdownData,
  billingAndCustomerInfo: initialBillingData,
};

export const fetchCartData = createSagaAction(`${sliceName}/fetchCartData`);
export const addAddonsAndTopUpsOnOrder = createSagaAction(
  `${sliceName}/addAddonsAndTopUpsOnOrder`,
);
export const sendBillingAndCustomerInfo = createSagaAction(
  `${sliceName}/sendBillingAndCustomerInfo`,
);

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    startCountdown: (state, action: PayloadAction<vendorIdAndEventId>) => {
      const endTime: string = DateTime.now().plus({ minutes: 10 }).toISO();
      return {
        ...state,
        countdown: {
          onGoing: true,
          endTime,
          vendorId: action.payload.vendorId,
          eventId: action.payload.eventId,
        },
        billingAndCustomerInfo: initialBillingData,
      };
    },
    stopCountdown: state => {
      const getInitialCountdownData = () => initialCountdownData;
      return {
        ...state,
        countdown: {
          ...getInitialCountdownData(),
        },
      };
    },
    startSeatsCountdown: (state, action: PayloadAction<HoldToken>) => {
      const endTime = DateTime.fromISO(action.payload.expiresAt).toISO();
      return {
        ...state,
        holdToken: {
          token: action.payload.token,
          endTime: endTime as string,
        },
      };
    },
    stopSeatsCountdown: state => {
      state.holdToken.token = '';
      state.holdToken.endTime = '';
    },
    setSelectedEventAndItems: (
      state,
      action: PayloadAction<{
        uuid: string;
        event: TicketSalePageEvent;
        tickets: ISelectedTicket[];
        voucher: VoucherInterface | null;
      }>,
    ) => {
      const { uuid, event, tickets, voucher } = action.payload;
      // set uuid
      state.uuid = uuid;
      // set event
      state.selectedEvent = {
        ...event,
        walletcredit: [],
      };

      if (voucher) {
        state.voucher = {
          ...voucher,
        };
      } else {
        const getInitialVoucher = () => initialVoucher;
        state.voucher = getInitialVoucher() as VoucherInterface;
      }

      state.selectedItems = tickets.map(ticket => {
        // get ticket type data
        const { name, ticketprice } = event.tickets.find(
          el => el.id === ticket.id,
        ) as Ticket;

        const { price, absorb_fees } = getTicketPriceByQuantity(
          ticketprice,
          ticket.quantity,
        );

        const tax = getTaxPrice(price, event.tax_rate.rate);
        const fee = getFeePrice(price, event.fees);
        const feeTax = getTaxPrice(fee, event.fee_tax_rate.rate);

        return {
          type: SelectedItemType.TICKET,
          ticketId: ticket.id,
          idOrderItem: ticket.idOrderItem,
          quantity: ticket.quantity,
          name,
          price,
          tax,
          fee,
          feeTax,
          absorbFees: absorb_fees,
          date: ticket.date,
          timeStart: ticket.timeStart,
          timeEnd: ticket.timeEnd,
        };
      });
    },
    setBillingInfo: (state, action: PayloadAction<IUserBilling>) => {
      const { email, name, companyName, address, city, cui, country, county } =
        action.payload;
      state.billingAndCustomerInfo = {
        email,
        billing: {
          type: cui ? 'company' : 'individual',
          name,
          company: companyName,
          tax_code: cui,
          address: {
            address,
            city,
            id_country: getCountryIdByCode(country) || '',
            id_state: getStateIdByCode(county) || '',
          },
        },
      };
    },
    clearCartData: state => {
      const getInitialData = () => initialState;
      return {
        ...getInitialData(),
        // on the actual configuration of SeatsIoChart, modifying the holdToken will re-render the component
        holdToken: {
          ...state.holdToken,
        },
      };
    },
  },
  extraReducers: {
    [fetchCartData.pending]: state => {
      state.loading = true;
      onIframeScrollIntoView();
    },
    [fetchCartData.fulfilled]: (
      state,
      action: PayloadAction<FetchCartDataPayload>,
    ) => {
      state.loading = false;
      const {
        serverResponse: { uuid, tickets: orderedTickets },
        tickets,
        bundles,
        selectedEvent,
        // addons,
        voucher,
      } = action.payload;

      // set uuid & event
      state.uuid = uuid;
      state.selectedEvent = selectedEvent;

      // set voucher
      if (voucher) {
        state.voucher = {
          ...voucher,
        };
      } else {
        const getInitialVoucher = () => initialVoucher;
        state.voucher = getInitialVoucher() as VoucherInterface;
      }

      // to refactor: the below code is duplicated inside TicketSalePage/slice/index.ts
      // set tickets & bundles
      const formattedTickets: CartTicket[] = tickets.map(ticket => ({
        ...ticket,
        idOrderItem: getIdOrderItemForTicket(
          ticket,
          selectedEvent,
          orderedTickets,
        ),
      }));

      const formattedBundles: CartBundle[] = bundles.map(bundle => ({
        ...bundle,
        orderItems: getOrderItemsForBundle(
          bundle,
          selectedEvent,
          orderedTickets,
        ),
      }));

      state.selectedItems = [...formattedTickets, ...formattedBundles];

      // Update 24.03.2025 - commented the below lines of code
      // because we plan to stop persisting data on the FE
      // -----
      // if the quantity was decreased we need to remove the selected
      // addons & top-ups of the selected ticket with the highest
      // ticketIndex value
      // const updatedAddons: CartItems = [];

      // for (let i = 0; i < formattedTickets.length; i++) {
      //   const boundedAddons = addons.filter(
      //     ({ ticketId }) => ticketId === formattedTickets[i].ticketId,
      //   );

      //   for (let j = 0; j < boundedAddons.length; j++) {
      //     if (
      //       boundedAddons[j].included ||
      //       boundedAddons[j].ticketIndex < formattedTickets[i].quantity
      //     ) {
      //       updatedAddons.push(boundedAddons[j]);
      //     }
      //   }
      // }

      // state.selectedItems = [...formattedTickets, ...updatedAddons];
    },
    [fetchCartData.rejected]: () => initialState,
    [addAddonsAndTopUpsOnOrder.pending]: state => {
      state.loading = true;
      onIframeScrollIntoView();
    },
    [addAddonsAndTopUpsOnOrder.fulfilled]: (state, action) => {
      state.loading = false;

      const { topUps, addons } = action.payload;

      // remove paid addons and top-ups
      const { selectedItems } = state;
      for (let i = 0; i < selectedItems.length; i++) {
        if (isAddon(selectedItems[i]) || isTopup(selectedItems[i])) {
          selectedItems.splice(i, 1);
          i--;
        }
      }

      const addonsToBeInserted: CartItems = [];

      if (addons) {
        for (let i = 0; i < addons.length; i++) {
          if (
            state.selectedItems.findIndex(
              item =>
                isAddon(item) &&
                item.ticketId === addons[i].ticketId &&
                item.addonId === addons[i].addonId &&
                item.ticketIndex === addons[i].ticketIndex,
            ) === -1
          ) {
            addonsToBeInserted.push(addons[i]);
          }
        }

        state.selectedItems = [...state.selectedItems, ...addonsToBeInserted];
      }

      if (topUps) {
        for (let key in topUps) {
          if (topUps[key]) {
            state.selectedItems.push({
              type: SelectedItemType.TOPUP,
              ticketId: key.split('-')[0],
              ticketIndex: parseInt(key.split('-')[1]),
              idOrderItem: key.split('-')[2],
              quantity: 1,
              name: 'Top-up',
              price: parseInt(topUps[key]),
              tax: 0,
              fee: 0,
              feeTax: 0,
              absorbFees: false,
            });
          }
        }
      }
    },
    [addAddonsAndTopUpsOnOrder.rejected]: () => initialState,
    [sendBillingAndCustomerInfo.pending]: state => {
      state.loading = true;
      onIframeScrollIntoView();
    },
    [sendBillingAndCustomerInfo.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.billingAndCustomerInfo = payload.body;
    },
    [sendBillingAndCustomerInfo.rejected]: () => initialState,
    [getBillingInfoAction.pending]: state => {
      state.loading = true;
    },
    [getBillingInfoAction.fulfilled]: (state, { payload }) => {
      state.loading = false;

      const {
        name,
        company,
        type,
        address,
        id_country,
        id_state,
        city,
        tax_code,
      } = payload;

      state.billingAndCustomerInfo = {
        ...state.billingAndCustomerInfo,
        billing: {
          type,
          name,
          company,
          tax_code,
          address: {
            address,
            city,
            id_country,
            id_state,
          },
        },
      };
    },
    [getBillingInfoAction.rejected]: state => {
      state.loading = false;
    },
    [refreshTheAccesTokenAction.pending]: state => {
      state.loading = true;
    },
    [refreshTheAccesTokenAction.fulfilled]: (state, { payload }) => {
      state.loading = false;
    },
    [refreshTheAccesTokenAction.rejected]: state => {
      state.loading = false;
    },
    'user/logoutUserAction': state => {
      state.billingAndCustomerInfo = {
        email: '',
        billing: {
          type: '',
          name: '',
          company: '',
          address: {
            address: '',
            city: '',
            id_country: '',
            id_state: '',
            tax_code: '',
          },
        },
        fields: {},
      };
    },
  },
});

export const {
  actions: {
    startCountdown,
    stopCountdown,
    startSeatsCountdown,
    stopSeatsCountdown,
    setSelectedEventAndItems,
    setBillingInfo,
    clearCartData,
  },
} = slice;

export default slice.reducer;
